import { ref } from "vue";

import { ErrorDialog } from "shared/boot/alert";
import { useNotify } from "shared/composables/useNotify";
import DialogService, {
  type DialogServiceOptions,
} from "shared/modals/DialogService";
import { DialogTheme } from "shared/modals/types";
import { useInsightsLoadingActions } from "shared/modules/Insights/composables/actions/useInsightsLoadingActions";
import { type InsightsAction } from "shared/modules/Insights/types";
import { type SelectAllId } from "shared/types";

export interface InsightsActionHandlerOptions {
  action: InsightsAction;
  confirmDialog?: DialogServiceOptions;
  disableAction?: boolean;
  errorMessage: string;
  ids: SelectAllId[];
  loadingMessage?: string;
  preserveDisabledAction?: boolean;
  successMessage?: string;
}

export function useInsightsActions() {
  const { loadingNotification, successNotification } = useNotify();

  const {
    addDisabledAction,
    addLoadingAction,
    disabledActions,
    loadingActions,
    removeDisabledAction,
    removeLoadingAction,
  } = useInsightsLoadingActions();

  const hasOpenConfirmDialog = ref<boolean>(false);

  function confirmAction(options: DialogServiceOptions): Promise<boolean> {
    hasOpenConfirmDialog.value = true;

    const result = DialogService({
      persistent: true,
      theme: DialogTheme.DANGER,
      ...options,
    });

    return new Promise<boolean>((resolve) => {
      result.onOk(() => {
        hasOpenConfirmDialog.value = false;
        resolve(true);
      });

      result.onCancel(() => {
        hasOpenConfirmDialog.value = false;
        resolve(false);
      });

      result.onDismiss(() => {
        hasOpenConfirmDialog.value = false;
        resolve(false);
      });
    });
  }

  function getInsightsActionHandler(
    options: InsightsActionHandlerOptions
  ): (callback: () => Promise<any>) => Promise<boolean> {
    return async (callback: () => Promise<any>) => {
      const {
        action,
        confirmDialog,
        disableAction,
        errorMessage,
        ids,
        loadingMessage,
        preserveDisabledAction,
        successMessage,
      } = options;

      if (ids.length === 0) {
        return false;
      }

      if (confirmDialog) {
        const confirmed = await confirmAction(confirmDialog);

        if (!confirmed) {
          return false;
        }
      }

      let dismissLoadingNotification: ReturnType<typeof loadingNotification> =
        null;

      if (loadingMessage) {
        dismissLoadingNotification = loadingNotification({
          message: loadingMessage,
        });
      }

      try {
        addLoadingAction(action, ids);

        if (disableAction) {
          addDisabledAction(action, ids);
        }

        await callback();

        if (successMessage) {
          successNotification({
            message: successMessage,
          });
        }

        return true;
      } catch (error) {
        ErrorDialog(errorMessage, error);

        return false;
      } finally {
        dismissLoadingNotification?.();
        removeLoadingAction(action, ids);

        if (disableAction && !preserveDisabledAction) {
          removeDisabledAction(action, ids);
        }
      }
    };
  }

  return {
    disabledActions,
    getInsightsActionHandler,
    hasOpenConfirmDialog,
    loadingActions,
    removeDisabledAction,
  };
}

export default useInsightsActions;
